<template>
  <v-app-bar app flat color="#18181c" style="border-bottom: 1px solid #2e2e38">

    <!-- Logo -->
    <div class="logo-wrap mr-4" @click="$router.push('/')" style="cursor:pointer">
      <div class="logo-mark">
        <v-icon small color="white">mdi-arrow-up-bold</v-icon>
      </div>
      <span class="logo-text">deal<span class="primary--text">hunt</span></span>
    </div>

    <!-- Nav buttons -->
    <v-btn text small to="/" exact class="nav-btn" active-class="nav-btn--active">
      <v-icon left small>mdi-fire</v-icon>
      Deals
    </v-btn>
    <v-btn text small to="/browser" class="nav-btn" active-class="nav-btn--active">
      <v-icon left small>mdi-magnify</v-icon>
      Browse
    </v-btn>
    <v-btn text small to="/wishlist" class="nav-btn" active-class="nav-btn--active">
      <v-icon left small>mdi-heart-outline</v-icon>
      Wishlist
      <v-chip
        v-if="wishlistCount > 0"
        x-small
        color="accent"
        class="ml-1 pa-1"
        style="height:16px;min-width:16px;font-size:10px"
      >{{ wishlistCount }}</v-chip>
    </v-btn>
    <v-btn text small to="/guide" class="nav-btn" active-class="nav-btn--active">
      <v-icon left small>mdi-book-open-page-variant</v-icon>
      Guide
    </v-btn>

    <v-spacer />

    <!-- Inline search hint -->
    <v-btn
      outlined
      small
      class="search-hint mr-2"
      @click="$router.push('/browser')"
    >
      <v-icon left small>mdi-magnify</v-icon>
      Search games…
    </v-btn>

    <!-- Auth button -->
    <login-button class="mr-2" />

    <!-- Bell: opens alert dialog -->
    <v-dialog v-model="alertDialog" max-width="420" persistent>
      <template #activator="{ on, attrs }">
        <v-badge
          :value="alertsCount > 0"
          :content="String(alertsCount)"
          color="accent"
          overlap
          offset-x="8"
          offset-y="8"
        >
          <v-btn icon v-bind="attrs" v-on="on" title="Manage price alerts">
            <v-icon>mdi-bell-outline</v-icon>
          </v-btn>
        </v-badge>
      </template>

      <v-card style="background:#1f1f24;border:1px solid #2e2e38">
        <v-toolbar flat color="primary" dark dense>
          <v-toolbar-title class="text-body-2 font-weight-bold">
            Active price alerts
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon small @click="closeDialog">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Empty state -->
        <v-card-text v-if="activeAlerts.length === 0" class="pt-6 pb-6 text-center">
          <v-icon size="40" color="#3a3a44">mdi-bell-sleep-outline</v-icon>
          <p class="caption mt-3 mb-0" style="color:#7b7a8e">
            You have no active price alerts. Open any deal and set one to be
            emailed when the price drops.
          </p>
        </v-card-text>

        <!-- Alert list -->
        <v-list v-else dense color="transparent" class="py-0">
          <v-list-item
            v-for="alert in activeAlerts"
            :key="alert.gameID"
            class="px-4"
          >
            <v-list-item-content>
              <v-list-item-title class="text-body-2" style="color:#f0eff6">
                {{ alert.title }}
              </v-list-item-title>
              <v-list-item-subtitle class="caption" style="color:#7b7a8e">
                Alert below ${{ alert.price }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                icon small color="error"
                :loading="removingID === alert.gameID"
                title="Remove alert"
                @click="removeAlert(alert.gameID)"
              >
                <v-icon small>mdi-bell-off-outline</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>

  </v-app-bar>
</template>

<script>
import { mapState } from 'vuex'
import LoginButton from './LoginButton.vue'
import { cancelPriceAlert } from '@/utils/alerts'

export default {
  name: 'NavMenu',
  components: { LoginButton },
  props: {
    title: { type: String, default: 'dealhunt' },
  },
  data () {
    return {
      alertDialog: false,
      removingID:  null,
    }
  },
  computed: {
    ...mapState(['wishlist', 'wishlistAlerts', 'currentUser']),
    wishlistCount () { return this.wishlist ? this.wishlist.length : 0 },
    alertsCount () { return Object.keys(this.wishlistAlerts || {}).length },
    // Flatten the { gameID: { price, title } } map into a render-friendly list.
    activeAlerts () {
      return Object.entries(this.wishlistAlerts || {}).map(([gameID, a]) => ({
        gameID,
        price: a.price,
        title: a.title || this.wishlistTitle(gameID) || gameID,
      }))
    },
  },
  methods: {
    // Fall back to the wishlist entry's title for alerts saved before titles
    // were stored alongside the alert.
    wishlistTitle (gameID) {
      const item = (this.wishlist || []).find(d => d.gameID === gameID)
      return item ? item.title : null
    },
    async removeAlert (gameID) {
      this.removingID = gameID
      try {
        await cancelPriceAlert({ currentUser: this.currentUser, gameID })
        this.$store.commit('clearWishlistAlert', gameID)
      } catch (err) {
        console.error('Failed to remove alert:', err)
      } finally {
        this.removingID = null
      }
    },
    closeDialog () {
      this.alertDialog = false
    },
  },
}
</script>

<style scoped>
.logo-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  user-select: none;
}

.logo-mark {
  width: 28px;
  height: 28px;
  background: #7c6ff7;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-text {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: #f0eff6;
}

.nav-btn {
  font-size: 13px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  color: #7b7a8e !important;
  text-transform: none !important;
  border-radius: 8px !important;
}

.nav-btn--active,
.nav-btn:hover {
  color: #f0eff6 !important;
  background: #2e2e38 !important;
}

.search-hint {
  font-size: 12px !important;
  font-weight: 400 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  color: #7b7a8e !important;
  border-color: #2e2e38 !important;
  border-radius: 8px !important;
  min-width: 160px;
  justify-content: flex-start;
}

.search-hint:hover {
  border-color: #7c6ff7 !important;
  color: #f0eff6 !important;
}
</style>
